import { Component, Input, NgZone, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { Data, Router } from '@angular/router';
import { AuthService } from '@core/auth.service';
import { AuthPartialRegService } from "@core/AuthServices/authPartialReg/auth-partial-reg.service";
import { UserVersionService } from "@core/AuthServices/authUserVersion/auth-user-version.service";
import { UtilityService } from '@core/utility.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { combineLatest, firstValueFrom, from, of, Subject, takeUntil } from 'rxjs';
import { catchError, finalize, take, timeout } from 'rxjs/operators';
import {
  buildLocalPushBannerContent,
  buildPushToastPayload,
  normalizePushConstantsToast,
  PushToastKey,
  readLanguageCodeFromStorage,
  resolvePushToastLanguage,
} from "@core/push-notification-toast.i18n";
import { CommonVariable } from 'src/app/app.constants';
import { FirestoreDataService } from "src/app/services/firestore-data.service";
import { OneSignalService, PushOptInResult } from 'src/app/services/one-signal.service';

@Component({
  selector: 'app-push-notification-banner',
  templateUrl: './push-notification-banner.component.html',
  styleUrls: ['./push-notification-banner.component.scss'],
  standalone: true,
  imports: [TranslatePipe]
})
export class PushNotificationBannerComponent implements OnInit, OnDestroy, OnChanges {

  private unsubscribe$: Subject<void> = new Subject<void>();
  public isFreeLineClaimed: boolean = false;
  /** False until account/device issued check emits — avoids claim-copy flash for issued users. */
  public issuedStateReady: boolean = false;
  public isNotificationSubscribed: boolean = false;
  @Input() notificationData!: Data;
  /** Language-resolved banner copy (from push_notification content map when available). */
  public displayContent: Data = {};
  public dismissedDevicesData: Array<Data> = [];
  public activatedDevicesData: Array<Data> = [];
  public userId: string;
  private isActivating = false;
  /** Cached multi-lang content map from users/{uid}/inline_notification/push_notification. */
  private pushContentMap: Data | null = null;
  /** Guards overlapping async content-map loads when language switches quickly. */
  private contentLangRequestId = 0;

  get showNotification(): boolean {
    return !this.isNotificationSubscribed || !this.isFreeLineClaimed;
  }

  constructor(
    private firestore: Firestore,
    private route: Router,
    public oneSignalService: OneSignalService,
    public translate: TranslateService,
    private authPartialRegService: AuthPartialRegService,
    private authService: AuthService,
    private userVersionService: UserVersionService,
    private firestoreDataService: FirestoreDataService,
    private ngZone: NgZone,
  ) {
    this.authService?.selectedlanguage$?.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (language: string) => {
        if (language) {
          translate.use(language || "en");
          this.applyDisplayContentForLang(language);
          void this.loadPushContentMapIfNeeded(language);
        }
      },
      error: (error) => {
        return error;
      },
    });

    //Get pushNotification Data
    this.authService.getPushNotificationData()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res: Data) => {
        if (!res) return;
        this.authService.push_pref.set(res?.push_pref ?? true);
        const arrays = this.authService.getPushDeviceArrays(res);
        this.dismissedDevicesData = arrays.dismissedDevices;
        this.activatedDevicesData = arrays.activatedDevices;
      },
      )
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Store/Syndicate re-resolve language and replace notificationData.content —
    // re-apply so the banner does not keep a stale English seed from ngOnInit.
    if (changes["notificationData"] && !changes["notificationData"].firstChange) {
      this.pushContentMap = null;
      this.seedDisplayContentFromInput();
      const lang = this.resolveToastLanguage();
      this.applyDisplayContentForLang(lang);
      void this.loadPushContentMapIfNeeded(lang);
    }
  }

  async ngOnInit() {
    this.seedDisplayContentFromInput();
    this.userId = this.normalizeUserId(
      await this.resolveUserId("PushNotificationBannerComponent")
    );
    const lang = this.resolveToastLanguage();
    this.applyDisplayContentForLang(lang);
    void this.loadPushContentMapIfNeeded(lang);

    if (this.userId) {
      const notification$ = this.oneSignalService?.showOneSignalNotificationUI$ || of(true);
      const syndicate$ = this.authPartialRegService?.checkOneSignalPushNotification(this.userId) || of(false);

      combineLatest({
        // To get the current status of onesignal notification UI
        showNotification: notification$,
        // check if the 2 free lines voucher has been claimed before
        syndicateIssued: syndicate$,
      })
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (res: { syndicateIssued: boolean; showNotification: boolean }) => {
            this.isFreeLineClaimed = res.syndicateIssued;
            this.isNotificationSubscribed = !res.showNotification;
            this.issuedStateReady = true;
            if (!(res.syndicateIssued === true || res.showNotification === false)) {
              const isClicked = false;
              this.trackNotification(isClicked);
            }
          },
          error: (error) => {
            this.issuedStateReady = true;
            this.authService?.errorHandlerLogs(
              "Error in getting push notification banner data",
              error
            );
          },
        });
    } else {
      this.issuedStateReady = true;
    }
  }

  /** Cached uid, then getUid$ (3s timeout), then getUid(). Timeout/null still tries getUid(). */
  private async resolveUserId(caller: string): Promise<string | null> {
    const fromField = this.normalizeUserId(this.userId);
    if (fromField) {
      return fromField;
    }
    try {
      const uid = await firstValueFrom(
        this.authService.getUid$(caller).pipe(
          take(1),
          timeout({ each: 3000 })
        )
      );
      const normalized = this.normalizeUserId(uid);
      if (normalized) {
        return normalized;
      }
    } catch {
      // Timeout, empty completion, or getUid$ error — try the promise API.
    }
    try {
      return this.normalizeUserId(await this.authService.getUid(caller));
    } catch {
      return null;
    }
  }

  private normalizeUserId(uid: unknown): string | null {
    if (uid == null) {
      return null;
    }
    // Accept string or string-like values (avoid rejecting valid cached ids)
    const asString = typeof uid === "string" ? uid : String(uid);
    const trimmed = asString.trim();
    if (!trimmed || trimmed === "null" || trimmed === "undefined" || trimmed === "[object Object]") {
      return null;
    }
    return trimmed;
  }

  private seedDisplayContentFromInput(): void {
    const inputContent = this.notificationData?.content;
    if (inputContent && typeof inputContent === "object") {
      this.displayContent = { ...inputContent };
    }
  }

  private isPushInlineBanner(): boolean {
    const id =
      this.notificationData?.notification_id?.id ||
      this.notificationData?.id;
    if (id === "push_notification") {
      return true;
    }
    // Same detection as Store/Syndicate isPushNotification().
    const actionDetails = this.notificationData?.action?.details?.toLowerCase?.() || "";
    return (
      actionDetails === "enable_push_notifications" ||
      actionDetails === "enable push notifications"
    );
  }

  /**
   * Prefer users/{uid}/inline_notification/push_notification content[lang]
   * so language switches do not keep stale English from main_pages flatten.
   */
  private async loadPushContentMapIfNeeded(lang: string): Promise<void> {
    const uid = this.normalizeUserId(this.userId);
    if (!uid || !this.isPushInlineBanner()) {
      return;
    }
    const requestId = ++this.contentLangRequestId;
    if (this.pushContentMap) {
      if (requestId === this.contentLangRequestId) {
        this.applyDisplayContentForLang(lang);
      }
      return;
    }
    try {
      const path =
        this.notificationData?.notification_id?.path ||
        `users/${uid}/inline_notification/push_notification`;
      const snap = await firstValueFrom(
        from(getDoc(doc(this.firestore, path))).pipe(
          take(1),
          timeout({ each: 3000 }),
          catchError(() => of(null))
        )
      );
      if (requestId !== this.contentLangRequestId) {
        return;
      }
      const data = snap && typeof (snap as Data).data === "function"
        ? (snap as Data).data()
        : null;
      const content = data?.content;
      if (content && typeof content === "object") {
        this.pushContentMap = content;
      }
    } catch (error) {
      this.authService?.errorHandlerLogs(
        "Error loading push_notification content map for banner language",
        error
      );
    }
    if (requestId === this.contentLangRequestId) {
      this.applyDisplayContentForLang(lang);
    }
  }

  private applyDisplayContentForLang(lang: string | null | undefined): void {
    const code = resolvePushToastLanguage(
      lang,
      readLanguageCodeFromStorage(),
      this.translate?.currentLang || this.translate?.defaultLang
    );
    const local = buildLocalPushBannerContent(code);
    const map = this.pushContentMap;
    if (map && typeof map === "object") {
      const langEntry = map[code];
      if (
        langEntry &&
        typeof langEntry === "object" &&
        !Array.isArray(langEntry) &&
        ((langEntry as Data).title != null ||
          (langEntry as Data).line_1 != null ||
          (langEntry as Data).line_1_alt != null)
      ) {
        // Local fills gaps (e.g. missing line_1_alt); Firestore wins when present.
        this.displayContent = { ...local, ...langEntry };
        return;
      }
      // Preferred lang missing from map — do not stick on English seed / content.en.
      if (code !== "en") {
        this.displayContent = local;
        return;
      }
      const enEntry = map.en;
      if (enEntry && typeof enEntry === "object" && !Array.isArray(enEntry)) {
        this.displayContent = { ...local, ...enEntry };
        return;
      }
    }
    // No multi-lang map: for non-en UI, use local copy instead of flat English input.
    if (code !== "en") {
      this.displayContent = local;
      return;
    }
    const inputContent = this.notificationData?.content;
    if (inputContent && typeof inputContent === "object") {
      this.displayContent = { ...local, ...inputContent };
      return;
    }
    this.displayContent = local;
  }

  /**
   * The function `trackNotification` tracks inline notifications with optional parameters for click and
   * close events.
   */
  trackNotification(isClicked: boolean, isClosed: boolean = false): void {
    this.authService?.trackInlineNotifications(CommonVariable.trackPushNotificationsData, isClicked, isClosed);
  }

  /**
   * Activate the push notification
   * @returns void
   */
  public async activateNotification() {
    try {
      if (this.isActivating) {
        return;
      }
      if (this.isNotificationSubscribed) {
        const [isClicked, isClosed] = [true, false];
        this.trackNotification(isClicked, isClosed);
      }
      // Optional chaining required: CommonVariable.device can be unset → throw → catch
      // skipped the rest of Activate (toasts never ran even when this.userId was set).
      // iOS Safari (not home-screen PWA) cannot receive web push — show install guidance.
      // Use authService toast (toastFacade shape). UserVersionService snackbar payload
      // crashes MyToastbar, so Activate looked non-clickable on Lambda iOS (Issue 3).
      if (
        !UtilityService.isAppInstalledOnHomePage() &&
        CommonVariable.device?.operatingSystem === "ios"
      ) {
        this.iosInstallToastMessage();
        return;
      }

      this.isActivating = true;

      // Use cached uid first so Activate does not depend on auth re-resolve
      let userId = this.normalizeUserId(this.userId);
      if (!userId) {
        userId = await this.resolveUserId("PushNotificationBannerActivate");
      }
      if (userId) {
        this.userId = userId;
      }

      if (!userId) {
        this.isActivating = false;
        this.errorToastMessage();
        return;
      }

      // Re-read account flag at click time so a slow/raced ngOnInit check cannot
      // skipPrizeAward=false and flash Subscribed! for already-issued accounts.
      const alreadyIssuedOnAccount = await this.resolveAlreadyIssuedOnAccount(userId);
      // Known issued accounts get immediate feedback. Unclaimed accounts wait for
      // the award response so "already issued" never shows prize-promising copy.
      if (alreadyIssuedOnAccount) {
        this.neutralToastMessage();
      }

      // Do not mark viewed / write activated_devices before optIn completes.
      // On Live the OneSignal path is slow; an early Firestore write can refresh
      // Store/Syndicate inline data, destroy this banner, and cancel the toast
      // subscription via takeUntil. Localhost skips the SDK so the race rarely shows.
      // Capture click-time uid so a mid-flight account switch cannot write
      // this device list onto another user's push_notification document.
      const activateForUserId = userId;

      this.oneSignalService.optIn(userId, {
        skipPrizeAward: alreadyIssuedOnAccount,
      })
        .pipe(
          take(1),
          finalize(() => {
            this.ngZone.run(() => {
              this.isActivating = false;
            });
          })
        )
        .subscribe({
          next: (result: PushOptInResult) => {
            this.ngZone.run(() => {
              void this.handleOptInSuccess(result, activateForUserId, alreadyIssuedOnAccount);
            });
          },
          error: (error) => {
            this.authService?.errorHandlerLogs(
              "Error while activating push notification",
              error
            );
            this.ngZone.run(() => {
              // Keep the early neutral toast for already-issued accounts.
              if (!alreadyIssuedOnAccount) {
                this.errorToastMessage();
              }
            });
          },
        });
    } catch (error) {
      this.isActivating = false;
      this.authService?.errorHandlerLogs(
        "Error while activating push notification",
        error
      );
      if (this.isFreeLineClaimed !== true) {
        this.errorToastMessage();
      }
    }
  }

  /**
   * Prefer in-memory issued state; if still false, re-query account flag (2s cap).
   */
  private async resolveAlreadyIssuedOnAccount(userId: string): Promise<boolean> {
    if (this.isFreeLineClaimed === true) {
      return true;
    }
    try {
      const issued = await firstValueFrom(
        this.authPartialRegService.checkPushFreeLinesIssues(userId).pipe(
          take(1),
          timeout({ each: 2000 }),
          catchError(() => of(false))
        )
      );
      if (issued === true) {
        this.isFreeLineClaimed = true;
        this.issuedStateReady = true;
        return true;
      }
    } catch {
      // Keep false — optIn / SQL already_issued path still corrects the toast.
    }
    return false;
  }

  /**
   * Apply Activate success UI. Re-checks auth uid before Firestore device writes
   * so a logout/login during Live opt-in cannot corrupt another account.
   */
  private async handleOptInSuccess(
    result: PushOptInResult,
    activateForUserId: string,
    alreadyIssuedOnAccount: boolean
  ): Promise<void> {
    // Live Incognito / no IndexedDB: OneSignal cannot opt in. Do not mark viewed
    // and do not overwrite an already-issued neutral toast with a generic error.
    if (result?.status === "sdk_unavailable") {
      if (!alreadyIssuedOnAccount) {
        this.privateBrowsingToastMessage();
      }
      return;
    }

    const currentUid = this.normalizeUserId(
      await this.authService.getUid("PushNotificationBannerActivateGuard")
    );
    const sameAccount = !!currentUid && currentUid === activateForUserId;

    if (sameAccount) {
      await this.updateInlineNotificationStatus("viewed");
    }

    if (result?.status === "already_issued") {
      this.isFreeLineClaimed = true;
      // Known-issued accounts already got the early neutral toast.
      if (!alreadyIssuedOnAccount) {
        this.neutralToastMessage();
      }
    } else if (result?.status === "success") {
      this.isFreeLineClaimed = true;
      // Defensive: never show prize-promising Subscribed! for known issued accounts.
      if (alreadyIssuedOnAccount) {
        return;
      }
      this.successToastMessage();
    } else if (!alreadyIssuedOnAccount) {
      // Opt-in completed with an unexpected status — still confirm.
      this.neutralToastMessage();
    }
  }

  async updateInlineNotificationStatus(key: string): Promise<void> {
    const fingerPrintID = CommonVariable.FingerprintJSConst || localStorage.getItem("fingerSalt");
    const sessionId = sessionStorage.getItem("SessionId");
    try {
      const route = this.getValidRoute();
      if (!route) return;//If Route is 

      const isDismissed = (key === "dismissed") ? true : false;
      const isActivated = (key === "viewed") ? true : false;
      const payload = {
        viewed: true,
        ...(isDismissed && { dismissed: true }) // Add 'dismissed' only if true
      };
      //Holds the  Updated value of Dismissed Devices data 
      const updatedDismissedData = {
        dismissed: isDismissed,
        session_id: sessionId,
      };

      //Holds the  Updated value of Activated Devices data 
      const updatedActivatedData = isActivated
        ? {
            activated: true,
            fingerprintId: fingerPrintID
          }
        : null;
      const notificatioID = this.notificationData?.notification_id?.id;
      // This function has been used to update push notification document
      await this.authService.updatePushNotificationData(
        this.activatedDevicesData,
        updatedDismissedData,
        updatedActivatedData,
        notificatioID
      );
      const notificationRoute = this.notificationData?.notification_id?.path;
      if (notificationRoute) {
        const doc_id = notificationRoute?.substring(notificationRoute.lastIndexOf("/") + 1) || null;
        const path_id = "pGxBYtRO4mdgNkboYdSy";
        const doc_data = payload;
        const dv = { docId: doc_id };
        const method = "update";
        this.authService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          dv,
          method,
        }).subscribe({
          error: (error) => {
            this.authService?.errorHandlerLogs(
              "Error updating Firestore document:",
              error
            );
            return error;
          }
        });
      }
    } catch (error) {
      this.authService?.errorHandlerLogs(
        "Caught error in updateInlineNotificationStatus:",
        error
      );
    }
  }

  successToastMessage() {
    this.authService?.showToastMessage(
      null,
      this.buildLocalizedToast("subscribed", {
        icon: "tick circle success.png",
        type: "Success",
      })
    );
  }

  /** Neutral toast when prize was already issued — no prize-promising copy */
  neutralToastMessage() {
    this.authService?.showToastMessage(
      null,
      this.buildLocalizedToast("notifications_on", {
        icon: "tick circle success.png",
        type: "Success",
      })
    );
  }

  errorToastMessage() {
    this.authService?.showToastMessage(
      null,
      this.buildLocalizedToast("something_went_wrong", {
        duration: 2000,
        icon: "tick circle error.svg",
        type: "Error",
      })
    );
  }

  /** Live private browsing / IndexedDB unavailable — OneSignal cannot opt in. */
  privateBrowsingToastMessage() {
    this.authService?.showToastMessage(
      null,
      this.buildLocalizedToast("push_unavailable_private", {
        duration: 4000,
        icon: "tick circle error.svg",
        type: "Error",
      })
    );
  }

  /** iOS Safari (non-PWA) cannot opt in — tell the user to add to home screen. */
  iosInstallToastMessage() {
    const defaultMsg = this.buildLocalizedToast("add_to_home_screen_push", {
      duration: 5000,
      icon: "tick circle error.svg",
      type: "Error",
    });
    // Prefer Global_lookup toast 1143 (multi-language) when available.
    this.authService
      ?.getConstantsMsg("1143")
      ?.then((translatedMsg) => {
        this.authService?.showToastMessage(
          normalizePushConstantsToast(translatedMsg, defaultMsg),
          defaultMsg
        );
      })
      .catch(() => {
        this.authService?.showToastMessage(null, defaultMsg);
      });
  }

  private resolveToastLanguage(): string {
    return resolvePushToastLanguage(
      this.authService?.selectedlanguage$?.value,
      readLanguageCodeFromStorage(),
      this.translate?.currentLang || this.translate?.defaultLang
    );
  }

  private buildLocalizedToast(
    key: PushToastKey,
    style: Record<string, unknown>
  ) {
    const lang = this.resolveToastLanguage();
    const ngxTranslated = this.translate?.instant?.(`vouchers.${key}`);
    return buildPushToastPayload(key, lang, style, ngxTranslated);
  }

  /**
   * The function `getValidRoute` returns a valid route from a URL based on predefined routes.
   */
  getValidRoute(): string {
    const route = this.route.url?.split("/")?.pop()?.split("?")?.[0];
    const routes = ["store", "syndicate", "game", "voucher"];
    if (!routes?.includes(route)) {
      return "";
    }
    return route;
  }

  /**
   * Dismiss the push notification banner
   */
  public async dismissNotification(): Promise<void> {
    await this.updateInlineNotificationStatus("dismissed");
    if (this.isNotificationSubscribed) {
      const [isClicked, isClosed] = [true, true];
      this.trackNotification(isClicked, isClosed);
    }
    this.isNotificationSubscribed = false;
    this.oneSignalService.isDismissed.set(true);
    this.oneSignalService.showOneSignalNotificationUI$.next(false);
  }

  getIconUrl(iconUrl: string | undefined): string {
    return this.authService.getIconUrl(iconUrl);
  }

  ngOnDestroy() {
    this.unsubscribe$?.next();
    this.unsubscribe$?.complete();
  }

}
